/* eslint-disable no-param-reassign */
import produce from 'immer';
import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGOUT,
  SILENT_LOGIN,
  UPDATE_PROFILE,
  GET_ACCOUNT_INFO,
  GET_USERS_LIST,
  GET_USERS_LIST_SUCCESS,
  GET_USERS_LIST_ERROR,
  SIGN_IN_AS_USER,
  SIGN_IN_AS_USER_ERROR,
  SIGN_IN_AS_USER_SUCCESS,
  CHANGE_COMPANY_TITLE,
  CHANGE_COMPANY_TITLE_ERROR,
  CHANGE_COMPANY_TITLE_SUCCESS,
  GET_EMAIL_TOKEN,
  GET_EMAIL_TOKEN_ERROR,
  GET_EMAIL_TOKEN_SUCCESS,
  CHANGE_CAMPAIGNS,
  CHANGE_CAMPAIGNS_ERROR,
  CHANGE_CAMPAIGNS_SUCCESS,
  SET_USER_WAS_SHOWN_ERRORS,
  SET_GET_USERS_EMAIL_TOKEN,
  SET_EMAIL_TOKEN_ERROR_MESSAGE,
  SOCKET_SMS_RATE_LIMITS,
  ENABLE_CP_TEXTING,
  ENABLE_CP_TEXTING_ERROR,
  ENABLE_CP_TEXTING_SUCCESS,
  DISMISS_ENABLE_CP_TEXTING,
  DISMISS_PB_TEXTING_MODAL,
  DISMISS_PB_TEXTING_MODAL_SUCCESS,
  DISMISS_PB_TEXTING_BANNER_SESSION,
  SHOW_PB_TEXTING_MODAL,
  DISMISS_WHATS_NEW,
  DISMISS_WHATS_NEW_BANNER_SESSION,
  SHOW_WHATS_NEW,
  UPDATE_WHATS_NEW,
  DISMISS_SHARE_SET_UPDATES,
  SET_WHATS_NEW_HISTORY,
  CLOSE_WHATS_NEW_HISTORY,
} from 'src/actions/accountActions';
import {
  GET_USER_SUBSCRIPTION_SUCCESS,
  SUBSCRIBE_SUCCESS,
  UNSUBSCRIBE_SUCCESS,
  CANCEL_SUBSCRIPTION_AT_PERIOD_END_SUCCESS,
  UPDATE_SUBSCRIPTION_PAYMENT_METHOD_SUCCESS,
  CREATE_REFERRAL_CODE_SUCCESS,
  APPLY_REFERRAL_CODE_SUCCESS,
  RETRIEVE_USER_SUBSCRIPTION,
  TELNYX_MIGRATION_PIN_CLEARED,
  TELNYX_MIGRATION_PIN_MODAL_REQUESTED,
} from 'src/actions/accountSettingsActions';

const initialState = {
  azAccountName: '',
  databaseBeingUsed: '',
  signInErrors: [],
  campaignNamesToChange: [],
  newUpdatedCampaignsList: [],
  updateUsersTitle: false,
  getUsersEmailToken: false,
  enableCPTexting: false,
  enableCPTextingLoading: false,
  cpTextingGuideUrl: null,
  userMessageSendTimes: null,
  emailProvider: '',
  message: null,
  screenMessage: null,
  companyTitles: [],
  updateCompanyTitleLoading: false,
  getUsersEmailTokenLoading: false,
  updateCampaignLoading: false,
  user: null,
  usersList: {
    users: [],
    loading: false,
  },
  userWasShownErrors: [],
  loginLoading: false,
  pbAPIKeyValid: true,
  signInAs: false,
  stids: null,
  rateLimits: {
    serverTime: null,
    textsSent: 0,
    dailyTextsSent: 0,
    rateLimit: 0,
    dailyTextSoftLimit: 0,
    dailyTextHardLimit: 0,
    waitTime: 0,
  },
  tcrFormLink: '',
  maxContactImportRows: 1000,
  // CON-1925 — the MMS channel's attachment policy from the login payload.
  // These defaults are the server's code defaults; the composers read
  // maxAttachments only when canSendMultipleAttachments(user) is true.
  mmsAttachmentPolicy: {
    maxAttachments: 1,
    totalBytes: 307200,
    allowedFileTypes: [],
  },
  csvImportLargeThreshold: 5000,
  csvImportMaxRevert: 5000,
  csvImportDisabled: false,
  mobileAppIosUrl: null,
  mobileAppAndroidUrl: null,
  zoomClassIntroUrl: null,
  zoomClassEventsUrl: null,
  pbTextingModal: {
    shouldShowModal: false,
    shouldShowBanner: false,
    bannerDismissedThisSession: false,
    videos: [],
  },
  whatsNew: {
    shouldShowModal: false,
    shouldShowBanner: false,
    showCount: 0,
    permanentlyDismissed: false,
    bannerDismissedThisSession: false,
    headerSettings: {},
    features: [],
  },
  whatsNewHistory: {
    features: [],
    isOpen: false,
  },
  // CON-1552 Chunk 6 Stage C — recipient sign-in notice: Sets with published
  // updates the recipient has not pulled (and is still subscribed to). Read-
  // only prompt; the update picker is Stage D.
  shareSetUpdates: {
    shouldShowModal: false,
    sets: [],
  },
  cpTextingGuideMonthsToShow: null,
  excludedSalespersonIds: null,
  helpPageWebinarVideoUrl: null,
  helpPageWebinarVideoDescription: null,
  extendedMonthsSalespersonIds: null,
  hostAppFacebookLiveURL: null,
  hostAppIosUrl: null,
  hostAppAndroidUrl: null,
  // Texting move to the new provider. Both come from the login and /me
  // payloads and are null when the user has nothing to do.
  // telnyxMigrationPin: { required, maskedPhone } -- drives the code modal.
  // telnyxMigrationBanner: { state, headline, body, ctaLabel, ctaUrl,
  //   dismissible } -- drives the top banner.
  telnyxMigrationPin: null,
  telnyxMigrationBanner: null,
  // Bumped when something asks the code modal to open (the banner's
  // "Enter code"). The modal watches it.
  telnyxMigrationPinModalRequest: 0,
};

const accountReducer = (state = initialState, action) => {
  const {
    payload,
    type
  } = action;
  switch (type) {
    case LOGIN_REQUEST: {
      return produce(state, (draft) => {
        // Ensure we clear current session
        draft.user = null;
        draft.updateCompanyTitleLoading = true;
        draft.getUsersEmailTokenLoading = true;
        draft.loginLoading = true;
      });
    }

    case LOGIN_SUCCESS: {
      const {
        user,
        azAccountName,
        databaseBeingUsed,
        stids,
        signInErrors = initialState.signInErrors,
        campaignNamesToChange = initialState.campaignNamesToChange,
        campaignsList = initialState.newUpdatedCampaignsList,
        updateUsersTitle = initialState.updateUsersTitle,
        getUsersEmailToken = initialState.getUsersEmailToken,
        emailProvider = initialState.emailProvider,
        companyTitles = initialState.companyTitles,
        pbAPIKeyValid = initialState.pbAPIKeyValid,
        rateLimits = initialState.rateLimits,
        maxContactImportRows = initialState.maxContactImportRows,
        mmsAttachmentPolicy = initialState.mmsAttachmentPolicy,
        csvImportLargeThreshold = initialState.csvImportLargeThreshold,
        csvImportMaxRevert = initialState.csvImportMaxRevert,
        csvImportDisabled = initialState.csvImportDisabled,
        tcrFormLink = initialState.tcrFormLink,
        enableCPTexting = initialState.enableCPTexting,
        cpTextingGuideUrl = initialState.cpTextingGuideUrl,
        userMessageSendTimes = initialState.userMessageSendTimes,
        mobileAppIosUrl,
        mobileAppAndroidUrl,
        zoomClassIntroUrl,
        zoomClassEventsUrl,
        pbTextingModal = initialState.pbTextingModal,
        whatsNew = initialState.whatsNew,
        shareSetUpdates = initialState.shareSetUpdates,
        cpTextingGuideMonthsToShow,
        excludedSalespersonIds,
        helpPageWebinarVideoUrl,
        helpPageWebinarVideoDescription,
        extendedMonthsSalespersonIds,
        hostAppFacebookLiveURL,
        hostAppIosUrl,
        hostAppAndroidUrl,
        telnyxMigrationPin = initialState.telnyxMigrationPin,
        telnyxMigrationBanner = initialState.telnyxMigrationBanner,
      } = payload;

      return produce(state, (draft) => {
        draft.user = user;
        draft.azAccountName = azAccountName;
        draft.databaseBeingUsed = databaseBeingUsed;
        draft.stids = stids;
        draft.signInErrors = signInErrors;
        draft.campaignNamesToChange = campaignNamesToChange;
        draft.newUpdatedCampaignsList = campaignsList;
        draft.updateUsersTitle = updateUsersTitle;
        draft.getUsersEmailToken = getUsersEmailToken;
        draft.enableCPTexting = enableCPTexting;
        draft.enableCPTextingLoading = false;
        draft.cpTextingGuideUrl = cpTextingGuideUrl;
        draft.emailProvider = emailProvider;
        draft.pbAPIKeyValid = pbAPIKeyValid;
        draft.companyTitles = companyTitles;
        draft.updateCompanyTitleLoading = false;
        draft.getUsersEmailTokenLoading = false;
        draft.userWasShownErrors = initialState.userWasShownErrors;
        draft.loginLoading = false;
        draft.rateLimits = rateLimits;
        draft.maxContactImportRows = maxContactImportRows;
        draft.mmsAttachmentPolicy = {
          ...initialState.mmsAttachmentPolicy,
          ...(mmsAttachmentPolicy || {}),
        };
        draft.csvImportLargeThreshold = csvImportLargeThreshold;
        draft.csvImportMaxRevert = csvImportMaxRevert;
        draft.csvImportDisabled = csvImportDisabled;
        draft.tcrFormLink = tcrFormLink;
        draft.userMessageSendTimes = userMessageSendTimes;
        draft.mobileAppIosUrl = mobileAppIosUrl;
        draft.mobileAppAndroidUrl = mobileAppAndroidUrl;
        draft.zoomClassIntroUrl = zoomClassIntroUrl;
        draft.zoomClassEventsUrl = zoomClassEventsUrl;
        draft.pbTextingModal = {
          ...initialState.pbTextingModal,
          ...pbTextingModal,
        };
        draft.whatsNew = {
          ...initialState.whatsNew,
          ...whatsNew,
        };
        draft.shareSetUpdates = {
          ...initialState.shareSetUpdates,
          ...shareSetUpdates,
        };
        draft.cpTextingGuideMonthsToShow = cpTextingGuideMonthsToShow;
        draft.excludedSalespersonIds = excludedSalespersonIds;
        draft.helpPageWebinarVideoUrl = helpPageWebinarVideoUrl;
        draft.helpPageWebinarVideoDescription = helpPageWebinarVideoDescription;
        draft.extendedMonthsSalespersonIds = extendedMonthsSalespersonIds;
        draft.hostAppFacebookLiveURL = hostAppFacebookLiveURL;
        draft.hostAppIosUrl = hostAppIosUrl;
        draft.hostAppAndroidUrl = hostAppAndroidUrl;
        draft.telnyxMigrationPin = telnyxMigrationPin;
        draft.telnyxMigrationBanner = telnyxMigrationBanner;
      });
    }

    case LOGIN_FAILURE: {
      return produce(state, (draft) => {
        draft.updateCompanyTitleLoading = false;
        draft.getUsersEmailTokenLoading = false;
        draft.loginLoading = false;
        // Maybe store error
      });
    }

    case LOGOUT: {
      return produce(state, (draft) => {
        draft.user = null;
        draft.userWasShownErrors = initialState.userWasShownErrors;
        draft.updateCompanyTitleLoading = false;
        draft.getUsersEmailTokenLoading = false;
        draft.signInAs = false;
        draft.rateLimits = initialState.rateLimits;
        draft.telnyxMigrationPin = initialState.telnyxMigrationPin;
        draft.telnyxMigrationBanner = initialState.telnyxMigrationBanner;
      });
    }

    case SILENT_LOGIN: {
      const {
        user,
        azAccountName,
        databaseBeingUsed,
        stids,
        signInErrors = initialState.signInErrors,
        pbAPIKeyValid = initialState.pbAPIKeyValid,
        rateLimits = initialState.rateLimits,
        maxContactImportRows = initialState.maxContactImportRows,
        mmsAttachmentPolicy = initialState.mmsAttachmentPolicy,
        csvImportLargeThreshold = initialState.csvImportLargeThreshold,
        csvImportMaxRevert = initialState.csvImportMaxRevert,
        csvImportDisabled = initialState.csvImportDisabled,
        tcrFormLink = initialState.tcrFormLink,
        enableCPTexting = initialState.enableCPTexting,
        userMessageSendTimes = initialState.userMessageSendTimes,
        mobileAppIosUrl,
        mobileAppAndroidUrl,
        zoomClassIntroUrl,
        zoomClassEventsUrl,
        pbTextingModal = initialState.pbTextingModal,
        whatsNew = initialState.whatsNew,
        shareSetUpdates = initialState.shareSetUpdates,
        cpTextingGuideMonthsToShow,
        excludedSalespersonIds,
        helpPageWebinarVideoUrl,
        helpPageWebinarVideoDescription,
        extendedMonthsSalespersonIds,
        hostAppFacebookLiveURL,
        hostAppIosUrl,
        hostAppAndroidUrl,
        telnyxMigrationPin = initialState.telnyxMigrationPin,
        telnyxMigrationBanner = initialState.telnyxMigrationBanner,
      } = payload;

      return produce(state, (draft) => {
        draft.user = user;
        draft.azAccountName = azAccountName;
        draft.databaseBeingUsed = databaseBeingUsed;
        draft.stids = stids;
        draft.signInErrors = signInErrors;
        draft.updateCompanyTitleLoading = false;
        draft.getUsersEmailTokenLoading = false;
        draft.pbAPIKeyValid = pbAPIKeyValid;
        draft.rateLimits = rateLimits;
        draft.maxContactImportRows = maxContactImportRows;
        draft.mmsAttachmentPolicy = {
          ...initialState.mmsAttachmentPolicy,
          ...(mmsAttachmentPolicy || {}),
        };
        draft.csvImportLargeThreshold = csvImportLargeThreshold;
        draft.csvImportMaxRevert = csvImportMaxRevert;
        draft.csvImportDisabled = csvImportDisabled;
        draft.tcrFormLink = tcrFormLink;
        draft.enableCPTexting = enableCPTexting;
        draft.userMessageSendTimes = userMessageSendTimes;
        draft.mobileAppIosUrl = mobileAppIosUrl;
        draft.mobileAppAndroidUrl = mobileAppAndroidUrl;
        draft.zoomClassIntroUrl = zoomClassIntroUrl;
        draft.zoomClassEventsUrl = zoomClassEventsUrl;
        draft.pbTextingModal = {
          ...initialState.pbTextingModal,
          ...pbTextingModal,
        };
        draft.whatsNew = {
          ...initialState.whatsNew,
          ...whatsNew,
        };
        draft.shareSetUpdates = {
          ...initialState.shareSetUpdates,
          ...shareSetUpdates,
        };
        draft.cpTextingGuideMonthsToShow = cpTextingGuideMonthsToShow;
        draft.excludedSalespersonIds = excludedSalespersonIds;
        draft.helpPageWebinarVideoUrl = helpPageWebinarVideoUrl;
        draft.helpPageWebinarVideoDescription = helpPageWebinarVideoDescription;
        draft.extendedMonthsSalespersonIds = extendedMonthsSalespersonIds;
        draft.hostAppFacebookLiveURL = hostAppFacebookLiveURL;
        draft.hostAppIosUrl = hostAppIosUrl;
        draft.hostAppAndroidUrl = hostAppAndroidUrl;
        draft.telnyxMigrationPin = telnyxMigrationPin;
        draft.telnyxMigrationBanner = telnyxMigrationBanner;
      });
    }

    case UPDATE_PROFILE: {
      const { user } = payload;

      return produce(state, (draft) => {
        draft.user = user;
      });
    }

    case GET_ACCOUNT_INFO: {
      const {
        user,
        azAccountName,
        databaseBeingUsed,
        pbAPIKeyValid,
        stids,
        rateLimits = initialState.rateLimits,
        maxContactImportRows = initialState.maxContactImportRows,
        mmsAttachmentPolicy = initialState.mmsAttachmentPolicy,
        csvImportLargeThreshold = initialState.csvImportLargeThreshold,
        csvImportMaxRevert = initialState.csvImportMaxRevert,
        csvImportDisabled = initialState.csvImportDisabled,
        tcrFormLink = initialState.tcrFormLink,
        userMessageSendTimes = initialState.userMessageSendTimes,
        telnyxMigrationPin = initialState.telnyxMigrationPin,
        telnyxMigrationBanner = initialState.telnyxMigrationBanner,
      } = payload;

      return produce(state, (draft) => {
        draft.user = user;
        draft.azAccountName = azAccountName;
        draft.databaseBeingUsed = databaseBeingUsed;
        draft.pbAPIKeyValid = pbAPIKeyValid;
        draft.telnyxMigrationPin = telnyxMigrationPin;
        draft.telnyxMigrationBanner = telnyxMigrationBanner;
        draft.stids = stids;
        draft.rateLimits = rateLimits;
        draft.maxContactImportRows = maxContactImportRows;
        draft.mmsAttachmentPolicy = {
          ...initialState.mmsAttachmentPolicy,
          ...(mmsAttachmentPolicy || {}),
        };
        draft.csvImportLargeThreshold = csvImportLargeThreshold;
        draft.csvImportMaxRevert = csvImportMaxRevert;
        draft.csvImportDisabled = csvImportDisabled;
        draft.tcrFormLink = tcrFormLink;
        draft.userMessageSendTimes = userMessageSendTimes;
      });
    }

    case GET_USERS_LIST: {
      return produce(state, (draft) => {
        draft.usersList.loading = true;
        draft.usersList.users = initialState.usersList.users;
      });
    }

    case GET_USERS_LIST_SUCCESS: {
      return produce(state, (draft) => {
        draft.usersList.loading = false;
        draft.usersList.users = payload;
      });
    }

    case GET_USERS_LIST_ERROR: {
      return produce(state, (draft) => {
        draft.usersList.loading = false;
        draft.usersList.users = initialState.usersList.users;
      });
    }

    case SIGN_IN_AS_USER: {
      return produce(state, (draft) => {
        draft.usersList.loading = true;
      });
    }

    case SIGN_IN_AS_USER_ERROR: {
      return produce(state, (draft) => {
        draft.usersList.loading = false;
        draft.updateCompanyTitleLoading = false;
        draft.getUsersEmailTokenLoading = false;
      });
    }

    case SIGN_IN_AS_USER_SUCCESS: {
      const {
        user,
        azAccountName,
        databaseBeingUsed,
        signInErrors = initialState.signInErrors,
        pbAPIKeyValid = initialState.pbAPIKeyValid,
        rateLimits = initialState.rateLimits,
        enableCPTexting = initialState.enableCPTexting,
        // Taken from the sign-in-as payload when present, otherwise cleared,
        // so an admin never sees her OWN texting-move prompt while signed in
        // as someone else.
        telnyxMigrationPin = initialState.telnyxMigrationPin,
        telnyxMigrationBanner = initialState.telnyxMigrationBanner,
      } = payload;
      return produce(state, (draft) => {
        draft.usersList.loading = false;
        draft.telnyxMigrationPin = telnyxMigrationPin;
        draft.telnyxMigrationBanner = telnyxMigrationBanner;
        draft.user = user;
        draft.azAccountName = azAccountName;
        draft.databaseBeingUsed = databaseBeingUsed;
        draft.signInErrors = signInErrors;
        draft.userWasShownErrors = initialState.userWasShownErrors;
        draft.updateCompanyTitleLoading = false;
        draft.getUsersEmailTokenLoading = false;
        draft.pbAPIKeyValid = pbAPIKeyValid;
        draft.signInAs = true;
        draft.rateLimits = rateLimits;
        draft.enableCPTexting = enableCPTexting;
      });
    }

    case CHANGE_COMPANY_TITLE: {
      return produce(state, (draft) => {
        draft.updateCompanyTitleLoading = true;
      });
    }

    case CHANGE_COMPANY_TITLE_ERROR: {
      return produce(state, (draft) => {
        draft.updateCompanyTitleLoading = false;
      });
    }

    case CHANGE_COMPANY_TITLE_SUCCESS: {
      return produce(state, (draft) => {
        draft.updateUsersTitle = false;
        draft.updateCompanyTitleLoading = false;
      });
    }

    case GET_EMAIL_TOKEN: {
      return produce(state, (draft) => {
        draft.getUsersEmailTokenLoading = true;
      });
    }

    case GET_EMAIL_TOKEN_ERROR: {
      return produce(state, (draft) => {
        draft.getUsersEmailTokenLoading = false;
        draft.message = payload?.message || null;
        draft.screenMessage = payload?.screenMessage || null;
      });
    }

    case GET_EMAIL_TOKEN_SUCCESS: {
      return produce(state, (draft) => {
        draft.getUsersEmailToken = false;
        draft.getUsersEmailTokenLoading = false;
        draft.message = null;
        draft.screenMessage = null;
      });
    }

    case CHANGE_CAMPAIGNS: {
      return produce(state, (draft) => {
        draft.updateCampaignLoading = true;
      });
    }

    case CHANGE_CAMPAIGNS_ERROR: {
      return produce(state, (draft) => {
        draft.updateCampaignLoading = false;
      });
    }

    case CHANGE_CAMPAIGNS_SUCCESS: {
      return produce(state, (draft) => {
        draft.updateCampaignLoading = false;
        draft.campaignNamesToChange = [];
      });
    }

    case SET_GET_USERS_EMAIL_TOKEN: {
      return produce(state, (draft) => {
        draft.getUsersEmailToken = payload;
      });
    }

    case SET_EMAIL_TOKEN_ERROR_MESSAGE: {
      return produce(state, (draft) => {
        draft.message = payload?.errorTitle || null;
        draft.screenMessage = payload?.errorMessage || null;
      });
    }

    case SET_USER_WAS_SHOWN_ERRORS: {
      return produce(state, (draft) => {
        draft.userWasShownErrors = payload ? [
          ...state.userWasShownErrors,
          ...payload,
        ] : initialState.userWasShownErrors;
      });
    }

    /* ******************
     * here we are listening accountSettingsActions
    ******************* */
    case APPLY_REFERRAL_CODE_SUCCESS:
    case CREATE_REFERRAL_CODE_SUCCESS:
    case GET_USER_SUBSCRIPTION_SUCCESS:
    case SUBSCRIBE_SUCCESS:
    case UNSUBSCRIBE_SUCCESS:
    case CANCEL_SUBSCRIPTION_AT_PERIOD_END_SUCCESS:
    case RETRIEVE_USER_SUBSCRIPTION:
    case UPDATE_SUBSCRIPTION_PAYMENT_METHOD_SUCCESS: {
      return produce(state, (draft) => {
        draft.user.userSubscription = payload ? {
          ...state?.user?.userSubscription || {},
          ...payload,
        } : state?.user?.userSubscription || {};
      });
    }
    /* END */

    case SOCKET_SMS_RATE_LIMITS: {
      return produce(state, (draft) => {
        draft.rateLimits = payload || initialState.rateLimits;
      });
    }

    case ENABLE_CP_TEXTING: {
      return produce(state, (draft) => {
        draft.enableCPTextingLoading = true;
      });
    }

    case ENABLE_CP_TEXTING_ERROR: {
      return produce(state, (draft) => {
        draft.enableCPTextingLoading = false;
      });
    }

    case ENABLE_CP_TEXTING_SUCCESS: {
      return produce(state, (draft) => {
        draft.enableCPTexting = false;
        draft.enableCPTextingLoading = false;
      });
    }

    case DISMISS_ENABLE_CP_TEXTING: {
      return produce(state, (draft) => {
        draft.enableCPTexting = false;
      });
    }

    case DISMISS_PB_TEXTING_MODAL:
    case DISMISS_PB_TEXTING_MODAL_SUCCESS: {
      return produce(state, (draft) => {
        draft.pbTextingModal.shouldShowModal = false;
        // Show banner after modal is dismissed (unless already dismissed this session)
        draft.pbTextingModal.shouldShowBanner = true;
      });
    }

    case TELNYX_MIGRATION_PIN_CLEARED: {
      return produce(state, (draft) => {
        draft.telnyxMigrationPin = null;
        // The code banner goes the moment the code is accepted, not when the
        // account refresh that brings the thanks banner comes back.
        if (draft.telnyxMigrationBanner?.state === 'pin_sent') {
          draft.telnyxMigrationBanner = null;
        }
      });
    }

    case TELNYX_MIGRATION_PIN_MODAL_REQUESTED: {
      return produce(state, (draft) => {
        draft.telnyxMigrationPinModalRequest += 1;
      });
    }

    case DISMISS_SHARE_SET_UPDATES: {
      return produce(state, (draft) => {
        draft.shareSetUpdates.shouldShowModal = false;
      });
    }

    case DISMISS_PB_TEXTING_BANNER_SESSION: {
      return produce(state, (draft) => {
        draft.pbTextingModal.bannerDismissedThisSession = true;
      });
    }

    case SHOW_PB_TEXTING_MODAL: {
      return produce(state, (draft) => {
        draft.pbTextingModal.shouldShowModal = true;
      });
    }

    case DISMISS_WHATS_NEW: {
      return produce(state, (draft) => {
        const { permanentlyDismiss } = action.payload || {};
        draft.whatsNew.shouldShowModal = false;
        if (permanentlyDismiss) {
          draft.whatsNew.shouldShowBanner = false;
          draft.whatsNew.permanentlyDismissed = true;
        } else {
          draft.whatsNew.shouldShowBanner = true;
        }
      });
    }

    case DISMISS_WHATS_NEW_BANNER_SESSION: {
      return produce(state, (draft) => {
        draft.whatsNew.bannerDismissedThisSession = true;
      });
    }

    case SHOW_WHATS_NEW: {
      return produce(state, (draft) => {
        draft.whatsNew.shouldShowModal = true;
      });
    }

    case UPDATE_WHATS_NEW: {
      return produce(state, (draft) => {
        const incoming = action.payload || {};
        // If polling returns new content, update features and show flags
        // but preserve bannerDismissedThisSession (session-only state)
        draft.whatsNew.features = incoming.features || [];
        draft.whatsNew.shouldShowModal = incoming.shouldShowModal || false;
        draft.whatsNew.showCount = incoming.showCount || 0;
        draft.whatsNew.permanentlyDismissed = incoming.permanentlyDismissed || false;
        if (incoming.headerSettings) {
          draft.whatsNew.headerSettings = incoming.headerSettings;
        }
        if (incoming.shouldShowBanner && !draft.whatsNew.bannerDismissedThisSession) {
          draft.whatsNew.shouldShowBanner = incoming.shouldShowBanner;
        }
      });
    }

    case SET_WHATS_NEW_HISTORY: {
      return produce(state, (draft) => {
        const { features = [] } = action.payload || {};
        draft.whatsNewHistory.features = features;
        draft.whatsNewHistory.isOpen = true;
      });
    }

    case CLOSE_WHATS_NEW_HISTORY: {
      return produce(state, (draft) => {
        draft.whatsNewHistory.isOpen = false;
      });
    }

    default: {
      return state;
    }
  }
};

export default accountReducer;
